import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listCustomFieldSchemas } from "@/api/lookups";
import type {
  CustomFieldEntityType,
  CustomFieldSchemaResponse,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import { CustomFieldForm } from "@/components/registries/CustomFieldForm";

export function CustomFieldsAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [entity, setEntity] = useState<CustomFieldEntityType>("client");
  const [editing, setEditing] = useState<CustomFieldSchemaResponse | null>(
    null,
  );
  const [creating, setCreating] = useState(false);

  const schemas = useQuery({
    queryKey: ["custom-fields", entity],
    queryFn: () => listCustomFieldSchemas(entity),
  });

  const columns: ColumnDef<CustomFieldSchemaResponse, unknown>[] = useMemo(
    () => [
      {
        id: "key",
        header: t("adminCustomFields.columns.key"),
        cell: (info) => info.row.original.key,
      },
      {
        id: "label",
        header: t("adminCustomFields.columns.label"),
        cell: (info) => info.row.original.label,
      },
      {
        id: "type",
        header: t("adminCustomFields.columns.type"),
        cell: (info) => info.row.original.field_type,
      },
      {
        id: "required",
        header: t("adminCustomFields.columns.required"),
        cell: (info) =>
          info.row.original.is_required ? t("common.yes") : t("common.no"),
      },
      {
        id: "filterable",
        header: t("adminCustomFields.columns.filterable"),
        cell: (info) =>
          info.row.original.is_filterable ? t("common.yes") : t("common.no"),
      },
      {
        id: "order",
        header: t("adminCustomFields.columns.order"),
        cell: (info) => info.row.original.order,
      },
      {
        id: "actions",
        header: "",
        cell: (info) => (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEditing(info.row.original)}
            data-testid={`edit-cf-${info.row.original.key}`}
          >
            {t("common.edit")}
          </Button>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <h1 className="text-xl font-semibold">
          {t("adminCustomFields.title")}
        </h1>
        <div className="flex items-center gap-2">
          <label className="text-sm">
            {t("adminCustomFields.entityFilter")}
            <select
              className="ml-2 h-9 rounded-md border bg-background px-2 text-sm"
              value={entity}
              onChange={(e) =>
                setEntity(e.target.value as CustomFieldEntityType)
              }
              data-testid="cf-entity-filter"
            >
              <option value="client">
                {t("adminCustomFields.entity.client")}
              </option>
              <option value="employee">
                {t("adminCustomFields.entity.employee")}
              </option>
            </select>
          </label>
          <Button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="open-create-cf"
          >
            {t("adminCustomFields.createButton")}
          </Button>
        </div>
      </header>
      <DataTable
        data={schemas.data ?? []}
        columns={columns}
        testId="custom-fields-table"
        isLoading={schemas.isLoading}
      />
      <CustomFieldForm
        open={creating || editing !== null}
        existing={editing}
        defaultEntity={entity}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        afterSubmit={() =>
          queryClient.invalidateQueries({ queryKey: ["custom-fields"] })
        }
      />
    </div>
  );
}
